/*
 * The Belgian Brewer: style-roots map (sections/tbb-beers.liquid).
 * Hover, click or keyboard focus on a beer in the index (or a click on a map marker) updates the
 * detail card and draws a dotted madder route from Bishop's Stortford to that beer's marker.
 * Without this script the card shows the signature beer and the map is a static chart.
 */
const START = [61.1, 90.1]; // Bishop's Stortford on the map
const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const f1 = (n) => n.toFixed(1);

function setUp(root) {
  if (root.dataset.tbbReady) return;
  root.dataset.tbbReady = 'true';

  let beers = [];
  let words = {};
  try {
    beers = JSON.parse(root.querySelector('[data-tbb-beer-data]')?.textContent || '[]');
    words = JSON.parse(root.querySelector('[data-tbb-beer-words]')?.textContent || '{}');
  } catch {
    return;
  }
  if (!beers.length) return;

  const q = (sel) => root.querySelector(sel);
  const detail = q('[data-tbb-detail]');
  const buttons = [...root.querySelectorAll('[data-tbb-beer]')];
  const markers = [...root.querySelectorAll('.mk')];
  const line = q('#tbb-routeLine');
  const halo = q('#tbb-routeHalo');
  const mask = q('#tbb-routeMaskPath');
  const tip = q('#tbb-routeTip');
  const field = (name) => detail.querySelector(`[data-tbb-d="${name}"]`);
  let current = -1;
  let ready = false;

  function clearRoute() {
    [line, halo, mask].forEach((p) => p?.setAttribute('d', ''));
    if (tip) tip.style.opacity = '0';
  }

  function route(target, animate) {
    if (!line || !mask || !tip) return;
    const [sx, sy] = START;
    const [tx, ty] = target;
    const dx = tx - sx;
    const dy = ty - sy;
    const length = Math.hypot(dx, dy);
    // a beer rooted at Bishop's Stortford itself (e.g. Pinot, Bailey Hills grapes) has no journey to draw
    if (length < 20) {
      clearRoute();
      return;
    }
    const k = 0.1 * length;
    const cx = (sx + tx) / 2 + (dy / length) * k;
    const cy = (sy + ty) / 2 - (dx / length) * k;
    let ux = tx - cx;
    let uy = ty - cy;
    const ul = Math.hypot(ux, uy);
    ux /= ul;
    uy /= ul;
    let vx = cx - sx;
    let vy = cy - sy;
    const vl = Math.hypot(vx, vy);
    vx /= vl;
    vy /= vl;
    const a = [sx + vx * 13, sy + vy * 13];
    const e = [tx - ux * 17, ty - uy * 17];
    const d = `M${f1(a[0])} ${f1(a[1])}Q${f1(cx)} ${f1(cy)} ${f1(e[0])} ${f1(e[1])}`;
    [line, halo, mask].forEach((p) => p?.setAttribute('d', d));
    tip.setAttribute(
      'transform',
      `translate(${f1(e[0] + ux * 4)} ${f1(e[1] + uy * 4)}) rotate(${f1((Math.atan2(uy, ux) * 180) / Math.PI)})`
    );

    if (animate) {
      mask.style.transition = 'none';
      mask.style.strokeDashoffset = '1';
      tip.style.transition = 'none';
      tip.style.opacity = '0';
      mask.getBoundingClientRect();
      mask.style.transition = 'stroke-dashoffset 1.25s cubic-bezier(.16,1,.3,1)';
      mask.style.strokeDashoffset = '0';
      tip.style.transition = '';
      tip.style.opacity = '1';
    } else {
      mask.style.transition = 'none';
      mask.style.strokeDashoffset = '0';
      tip.style.transition = 'none';
      tip.style.opacity = '1';
    }
  }

  function pick(index) {
    const beer = beers[index];
    if (!beer || index === current) return;
    buttons.forEach((b) => b.setAttribute('aria-pressed', String(Number(b.dataset.tbbBeer) === index)));
    field('name').textContent = beer.name;
    field('abv').textContent = `${beer.abv}%${beer.sig && words.signature ? ` · ${words.signature}` : ''}`;
    field('note').textContent = beer.note || '';
    field('root').textContent = beer.root || '';
    field('coords').textContent = `${beer.coords || ''}${words.approx || ''}`;
    field('story').textContent = beer.story || '';
    const awards = field('awards');
    if (awards) {
      awards.textContent = '';
      (beer.awards || []).forEach((award) => {
        const item = document.createElement('li');
        const leaf = document.createElement('span');
        leaf.className = 'tbb-lf';
        item.append(leaf, award);
        awards.appendChild(item);
      });
      awards.hidden = !(beer.awards || []).length;
    }
    field('glyph').setAttribute('href', `#tbb-g-${beer.glyph}`);
    const buy = field('url');
    if (buy && beer.url) buy.setAttribute('href', beer.url);
    detail.style.setProperty('--c', beer.colour);
    detail.classList.toggle('is-dark', Boolean(beer.dark));
    detail.classList.toggle('is-label', Boolean(beer.label));
    markers.forEach((m) => m.classList.toggle('on', m.dataset.tbbMk.split(' ').includes(String(index))));
    // beers without a map position (not yet placed, or not a Belgian style) simply get no route
    const onMap = beer.x != null && beer.y != null;
    if (onMap) {
      route([Number(beer.x), Number(beer.y)], ready && !reduceMotion());
    } else {
      clearRoute();
    }
    // hide lines the beer has no content for yet (e.g. a new beer without a story)
    Object.entries({ note: beer.note, root: beer.root, coords: beer.coords, story: beer.story }).forEach(([key, value]) => {
      const el = field(key);
      if (el) (el.closest('dl > div') || el).hidden = !value;
    });
    current = index;
  }

  buttons.forEach((button) => {
    const index = Number(button.dataset.tbbBeer);
    ['click', 'mouseenter', 'focus'].forEach((type) => button.addEventListener(type, () => pick(index)));
  });
  markers.forEach((marker) =>
    marker.addEventListener('click', () => pick(Number(marker.dataset.tbbMk.split(' ').pop())))
  );

  const pressed = buttons.find((b) => b.getAttribute('aria-pressed') === 'true');
  pick(pressed ? Number(pressed.dataset.tbbBeer) : 0);
  ready = true;
}

const init = (root = document) => root.querySelectorAll('[data-tbb-beers]').forEach(setUp);
init();
document.addEventListener('shopify:section:load', (event) => init(event.target));
